import React, { useEffect, useId, useMemo, useRef, useState } from 'react';

export default function InfoTooltip({
  text,
  ariaLabel = 'Info',
  maxWidthClassName = 'w-72',
}) {
  const id = useId();
  const rootRef = useRef(null);

  const [hovering, setHovering] = useState(false);
  const [focused, setFocused] = useState(false);
  const [clickOpen, setClickOpen] = useState(false);

  const open = hovering || focused || clickOpen;

  const tooltipId = useMemo(() => `infotooltip-${id}`, [id]);

  useEffect(() => {
    if (!clickOpen) return;

    const onPointerDown = (e) => {
      const root = rootRef.current;
      if (!root) return;
      if (root.contains(e.target)) return;
      setClickOpen(false);
    };

    const onKeyDown = (e) => {
      if (e.key === 'Escape') setClickOpen(false);
    };

    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [clickOpen]);

  const content = String(text || '').trim();
  if (!content) return null;

  return (
    <span ref={rootRef} className="relative inline-flex">
      <button
        type="button"
        aria-label={ariaLabel}
        aria-describedby={open ? tooltipId : undefined}
        aria-expanded={open}
        className="inline-flex items-center justify-center w-5 h-5 rounded-full border text-[11px] leading-none select-none"
        style={{
          borderColor: 'var(--ui-border)',
          background: 'var(--ui-surface)',
          color: 'var(--ui-text-muted)',
        }}
        onPointerDown={(e) => {
          e.stopPropagation();
        }}
        onMouseEnter={() => setHovering(true)}
        onMouseLeave={() => setHovering(false)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setClickOpen((v) => !v);
        }}
      >
        i
      </button>

      {open ? (
        <span
          id={tooltipId}
          role="tooltip"
          className={`absolute left-0 top-full mt-2 z-50 ${maxWidthClassName} max-w-[80vw]`}
          style={{ pointerEvents: 'none' }}
        >
          <span
            className="block rounded border p-2 text-xs whitespace-pre-wrap shadow-lg"
            style={{
              borderColor: 'var(--ui-border)',
              background: 'var(--ui-surface)',
              color: 'var(--ui-text)',
            }}
          >
            {content}
          </span>
        </span>
      ) : null}
    </span>
  );
}
